import { getFormatMoment } from '@/util/helpers/time-helpers';
import {
  MODAL_DATA_LOADED,
  MODAL_HIDDEN,
  MODAL_INTERACTION,
} from '../actions/constants';
import ColumnTypeData, {
  BOOL,
  DATE,
  DATETIME,
  FLOAT,
  INT,
  UUID,
} from '../util/ColumnType';
import {
  AFTER,
  BEFORE,
  CONTAINS,
  NOT,
  NOT_CONTAINS,
  NOT_ONE_OF,
} from '../util/Operators';
import {
  DAY,
  MONTH,
  QUARTER,
  WEEK,
  YEAR,
} from '../util/enums/EnumDateTimeAggregation';
import AggregateColumn from '../util/AggregateColumn';
import { WHERE } from '../util/enums/EnumLayers';
import Column from '../util/Column';
import ModalWhere, {
  ALL_SPECIFIC_TIMES,
} from '../components/modals/ModalWhere';

export const INITIAL_WHERE_STATE = {
  lhs: null, // ColumnName
  expressionType: null, // ColumnType
  operator: null, // string (see Operators.js)
  rhs: null, // string representing a selected option
  rhsText: null, // string representing a manually entered value
  rhsColumn: null, // ColumnName
  readyForSave: false,
  errorMsg: null,
  showColumnOptions: false,
  search: '',
};

export const INITIAL_GLOBAL_STATE = {
  clauses: [INITIAL_WHERE_STATE], // list of where clauses
  isAnd: false, // true if expression is AND.  Otherwise OR.
  readyForSave: false,
};

function interaction(state, payload) {
  switch (payload.section) {
    case 'search':
      return { ...state, search: payload.value };
    case 'lhs':
      return {
        ...state,
        lhs: AggregateColumn.fromColumn(
          payload.columnName,
          payload.extra.type() === DATETIME ? DAY : null, // default datetime columns to be aggregated by DAY
        ),
        // we've mapped 'extra' to the ColumnType
        expressionType:
          payload.extra.type() === DATETIME // default datetime columns to be aggregated by DAY (which is of type DATE)
            ? new ColumnTypeData(DATE, payload.extra.nullable())
            : payload.extra,
      };
    case 'lhsSelected':
      return {
        ...state,
        lhs: null,
        expressionType: null,
        operator: null,
        rhs: null,
        rhsText: null,
        rhsColumn: null,
        showColumnOptions: false,
      };
    case 'changeAgg': {
      const newAggCol = new AggregateColumn(
        payload.aggCol.alias(),
        payload.aggCol.name(),
        payload.aggregation,
      );
      return {
        ...state,
        lhs: newAggCol,
        expressionType: payload.extra,
        operator: null,
        rhs: null,
        rhsText: null,
        rhsColumn: null,
        showColumnOptions: false,
      };
    }
    case 'operator':
      return { ...state, operator: payload.operator };
    case 'operatorSelected':
      return {
        ...state,
        operator: null,
        rhs: null,
        rhsText: null,
        rhsColumn: null,
        showColumnOptions: false,
      };
    case 'rhs': {
      let { option } = payload;
      switch (payload.option) {
        case 'EMPTY':
          option = 'EMPTY';
          break;
        case 'x hours ago':
          option = '1 hour ago';
          break;
        case 'x days ago':
          option = '1 day ago';
          break;
        case 'x weeks ago':
          option = '1 week ago';
          break;
        case 'x months ago':
          option = '1 month ago';
          break;
      }
      return { ...state, rhs: option, rhsText: null, rhsColumn: null };
    }
    case 'rhsSelected':
      return { ...state, rhs: null, rhsText: null, rhsColumn: null };
    case 'rhsValueChanged':
      return {
        ...state,
        rhsText: payload.newValue.trim().length === 0 ? null : payload.newValue,
      };
    case 'rhsColumn': {
      if (payload.extra.shouldSwitchToDateTime) {
        return {
          ...state,
          expressionType: new ColumnTypeData(
            DATETIME,
            state.expressionType.nullable(),
          ),
          lhs: AggregateColumn.fromColumn(state.lhs.columnName(), null), // null means DATETIME
          rhsColumn: payload.columnName,
        };
      }
      return { ...state, rhsColumn: payload.columnName };
    }
    case 'showColumns':
      return { ...state, showColumnOptions: true };
    default:
      return state;
  }
}

const DATETIME_FORMAT_REGEX = /^\d\d\d\d-\d\d-\d\d \d\d:\d\d$/;
const DATE_FORMAT_REGEX = /^\d\d\d\d-\d\d-\d\d$/;
const MONTH_FORMAT_REGEX = /^\d\d\d\d-\d\d$/;
const QUARTER_FORMAT_REGEX = DATE_FORMAT_REGEX;
const YEAR_FORMAT_REGEX = /^\d\d\d\d$/;
const UUID_FORMAT_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const INT_FORMAT_REGEX = /^-?\d+$/;
const FLOAT_FORMAT_REGEX = /^-?\d+(\.\d+)?$/;

const updateState = (clauseIndex, state, stateDelta) => {
  return {
    ...state,
    clauses: [
      ...state.clauses.slice(0, clauseIndex),
      { ...state.clauses[clauseIndex], ...stateDelta },
      ...state.clauses.slice(clauseIndex + 1),
    ],
  };
};

const modalWhere = (state = INITIAL_GLOBAL_STATE, action) => {
  switch (action.type) {
    case MODAL_HIDDEN:
      return INITIAL_GLOBAL_STATE; // reset modal
    case MODAL_DATA_LOADED:
      if (action.payload.modal === ModalWhere) {
        if (action.payload.relevantColumnName) {
          const relevantColumn = action.payload.json
            .map((c) => Column.build(c))
            .find((c) => c.name().equals(action.payload.relevantColumnName));
          if (!relevantColumn.type().operators()) {
            return updateState(0, state, {
              errorMsg: `Currently no support for filtering on type: ${relevantColumn
                .type()
                .type()}`,
            });
          }
          const aggType =
            relevantColumn.type().type() === DATETIME ? DAY : null; // default to DAY for dates, as that's usually what people want
          return updateState(0, state, {
            lhs: AggregateColumn.fromColumn(
              action.payload.relevantColumnName,
              aggType,
            ),
            expressionType:
              aggType === DAY
                ? new ColumnTypeData(DATE, relevantColumn.type().nullable())
                : relevantColumn.type(),
          });
        }
        if (action.payload.existingLayer) {
          return stateForExistingLayer(action.payload.existingLayer);
        }
      }
      return state;
    case MODAL_INTERACTION: {
      if (action.payload.modalType !== WHERE) {
        return state;
      }
      return updateClauses(state, action);
    }
    default:
      return state;
  }
};

export function updateClauses(state, action) {
  if (action.payload.props.section === 'or') {
    // add a new section
    return {
      ...state,
      clauses: [
        ...state.clauses,
        {
          ...INITIAL_WHERE_STATE, // set chosen column to be same as above
          lhs: state.clauses[state.clauses.length - 1].lhs,
          expressionType:
            state.clauses[state.clauses.length - 1].expressionType,
          operator: state.clauses[state.clauses.length - 1].operator,
        },
      ],
      readyForSave: false,
    };
  }
  if (action.payload.props.section === 'toggleAnd') {
    return { ...state, isAnd: !state.isAnd };
  }
  const { clauseIndex } = action.payload.props;
  if (action.payload.props.section === 'delete') {
    // remove the section
    const result = {
      ...state,
      clauses: [
        ...state.clauses.slice(0, clauseIndex),
        ...state.clauses.slice(clauseIndex + 1),
      ],
    };
    result.readyForSave = result.clauses.every((c) => c.readyForSave);
    return result;
  }
  const newState = interaction(
    state.clauses[clauseIndex],
    action.payload.props,
  );

  // check if we should show an error message
  newState.errorMsg = null;
  if (newState.rhsText) {
    const lines = newState.rhsText.trim().split('\n');
    for (const val of lines) {
      if (lines.length > 10000) {
        newState.errorMsg = `Filter currently supports max 10,000 items.  You have entered: ${lines.length}`;
        break;
      }
      switch (newState.expressionType.type()) {
        case INT:
          if (!INT_FORMAT_REGEX.test(val.trim())) {
            newState.errorMsg = `Input is not a valid integer: ${val}`;
          }
          break;
        case FLOAT:
          if (!FLOAT_FORMAT_REGEX.test(val.trim())) {
            newState.errorMsg = `Input is not a valid decimal value: ${val}`;
          }
          break;
        case DATETIME:
          switch (newState.lhs.aggregation()) {
            case null:
              if (!DATETIME_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: yyyy-MM-dd HH-mm: ${val}`;
                // TODO: use moment() for this (Date.parse is unreliable - and doesn't work in safari and firefox)
              }
              break;
            case DAY:
              if (!DATE_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: ${getFormatMoment(
                  newState.lhs.aggregation(),
                )}`;
              }
              break;
            case WEEK:
              if (!DATE_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: ${getFormatMoment(
                  newState.lhs.aggregation(),
                )}`;
              }
              break;
            case MONTH:
              if (!MONTH_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: ${getFormatMoment(
                  newState.lhs.aggregation(),
                )}`;
              }
              break;
            case QUARTER:
              if (!QUARTER_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: ${getFormatMoment(
                  newState.lhs.aggregation(),
                )}`;
              }
              break;
            case YEAR:
              if (!YEAR_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: ${getFormatMoment(
                  newState.lhs.aggregation(),
                )}`;
              }
              break;
            default:
              if (!Date.parse(val.trim().split(' ')[0])) {
                newState.errorMsg = 'Invalid date';
              }
          }
          break;
        case DATE:
          switch (newState.lhs.aggregation()) {
            case null:
            case DAY:
              if (!DATE_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: ${getFormatMoment(
                  DAY,
                )}`;
              }
              break;
            case WEEK:
              if (!DATE_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: ${getFormatMoment(
                  WEEK,
                )}`;
              }
              break;
            case MONTH:
              if (!MONTH_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: ${getFormatMoment(
                  MONTH,
                )}`;
              }
              break;
            case QUARTER:
              if (!QUARTER_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: ${getFormatMoment(
                  QUARTER,
                )}`;
              }
              break;
            case YEAR:
              if (!YEAR_FORMAT_REGEX.test(val.trim())) {
                newState.errorMsg = `Date does not match expected format: ${getFormatMoment(
                  YEAR,
                )}`;
              }
              break;
            default:
              if (!Date.parse(val.trim().split(' ')[0])) {
                newState.errorMsg = 'Invalid date';
              }
          }
          break;
        case UUID:
          if (
            ![CONTAINS, NOT_CONTAINS].includes(newState.operator) &&
            !UUID_FORMAT_REGEX.test(val.trim())
          ) {
            newState.errorMsg = `Input is not a valid UUID: ${val}`;
          }

          break;
      }
      if (newState.errorMsg) {
        break; //
      }
    }
  }
  // calculate readyForSave for current clause
  newState.readyForSave = Boolean(
    newState.lhs &&
      newState.operator &&
      (newState.rhs || newState.rhsText || newState.rhsColumn) &&
      !newState.errorMsg,
  );

  const updatedState = { ...state };

  // if operator of first clause is a negation, then change isAnd to true (as usually you'll want to do "is not x AND is not y")
  if (
    updatedState.clauses.length === 1 &&
    action.payload.props.section === 'operator'
  ) {
    updatedState.isAnd = [
      NOT,
      NOT_CONTAINS,
      NOT_ONE_OF,
      BEFORE,
      AFTER,
    ].includes(newState.operator);
  }

  const result = updateState(clauseIndex, updatedState, newState);

  // modal's readyForSave is true if all clauses are readyForSave
  result.readyForSave = result.clauses.every((c) => c.readyForSave);
  return result;
}

export function stateForExistingLayer(existingLayer) {
  // WHERE layers consist of multiple clauses.  The first of which is the layer itself
  const clauses = [existingLayer];
  // extract the remaining clauses
  existingLayer.clauses().forEach((c) => {
    clauses.push(c);
  });
  // initialise modal with all the clauses
  return {
    ...INITIAL_GLOBAL_STATE,
    clauses: clauses.map((whereLayer) => {
      let rhs = null;
      let rhsText = null;
      let rhsColumn = null;
      if (whereLayer.rhsColumn()) {
        rhsColumn = whereLayer.rhsColumn();
      } else if (!whereLayer.rhsValue()) {
        rhs = whereLayer.rhsValue() === '' ? 'EMPTY' : 'NULL';
      } else if (whereLayer.expressionType().type() === BOOL) {
        rhs = whereLayer.rhsValue();
      } else if (
        ((whereLayer.expressionType().type() === DATETIME ||
          whereLayer.expressionType().type() === DATE) &&
          (whereLayer.rhsValue().indexOf('ago') !== -1 ||
            whereLayer.rhsValue().indexOf('from now') !== -1)) ||
        ALL_SPECIFIC_TIMES.includes(whereLayer.rhsValue())
      ) {
        rhs = whereLayer.rhsValue();
      } else {
        rhsText = whereLayer.rhsValue();
      }
      return {
        ...INITIAL_WHERE_STATE,
        lhs: whereLayer.lhs(),
        expressionType: whereLayer.expressionType(),
        operator: whereLayer.operator(),
        rhs,
        rhsText,
        rhsColumn,
        readyForSave: true,
        errorMsg: null,
      };
    }),
    isAnd: existingLayer.isAnd(),
    readyForSave: true,
  };
}

export default modalWhere;
